'use client';

import { useEffect, useState, type RefObject } from 'react';

export interface AnchoredDropdownStyle {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  minWidth: number;
  width?: number;
  /** Caps the list to the space available. Only set for type-aheads (see preferBelow). */
  maxHeight?: number;
}

interface UseAnchoredDropdownPositionArgs {
  /** Whether the dropdown is currently open. */
  open: boolean;
  /** Skip positioning entirely (e.g. the mobile bottom-sheet presentation). */
  disabled: boolean;
  /** The trigger element the dropdown is anchored under. */
  anchorRef?: RefObject<HTMLElement | null>;
  /** The dropdown element itself (measured for flip decisions). */
  dropdownRef: RefObject<HTMLElement | null>;
  /**
   * Size the dropdown to exactly the anchor's width so long option labels wrap
   * instead of stretching the list past its host (PB-2909). When false, the list
   * is shrink-to-fit with a 200px floor.
   */
  matchAnchorWidth: boolean;
  /**
   * Shrink to fit rather than flip over the anchor. Set for type-aheads only: covering the
   * field being typed into is unusable, whereas a plain Select loses nothing by flipping and
   * gains a taller list.
   */
  preferBelow: boolean;
}

const LIST_MIN_WIDTH_PX = 200; // 12.5rem at 16px
const GAP = 4;
/**
 * Below this much room under the field, staying anchored below stops being useful and it is
 * worth covering the anchor instead. Roughly two option rows.
 */
const MIN_USABLE_BELOW_PX = 120;
/** Never collapse to nothing — a very short scrollable list still beats an invisible one. */
const MIN_HEIGHT_PX = 96;

/**
 * How far the visible viewport is panned inside the layout viewport, on WebKit only.
 *
 * ## Invariants:
 * - Non-zero only on WebKit (every iOS browser, Chrome included). Why: there
 *   `getBoundingClientRect()` is measured from the VISIBLE viewport, while a `position: fixed`
 *   element is placed in the LAYOUT viewport. When the soft keyboard opens and iOS pans the page
 *   to keep the field in view, the two differ by `visualViewport.offsetTop` — often 200–300px —
 *   and a list placed at the raw rect lands that far up, on top of the field (PB-2909). Blink
 *   measures both in the layout viewport, so adding the offset there would double-count it.
 * - Detected by engine feature, not user agent — the same probe Floating UI uses for this exact
 *   correction. Only WebKit supports the prefixed backdrop filter; Blink and Gecko do not.
 */
const webkitVisualOffsets = (): { x: number; y: number } => {
  const vv = window.visualViewport;
  const isWebKit =
    typeof CSS !== 'undefined' &&
    typeof CSS.supports === 'function' &&
    CSS.supports('-webkit-backdrop-filter', 'none');
  if (!isWebKit || !vv) return { x: 0, y: 0 };
  return { x: vv.offsetLeft ?? 0, y: vv.offsetTop ?? 0 };
};

const sameStyle = (a: AnchoredDropdownStyle, b: AnchoredDropdownStyle) =>
  a.top === b.top &&
  a.bottom === b.bottom &&
  a.left === b.left &&
  a.right === b.right &&
  a.minWidth === b.minWidth &&
  a.width === b.width &&
  a.maxHeight === b.maxHeight;

/**
 * Positions a `position: fixed` dropdown under (or, when space is short, above) its
 * anchor, and keeps it glued to the anchor for as long as it is open.
 *
 * ## Invariants:
 * - Re-measures on captured document scrolls, window resizes, and visualViewport
 *   resize/scroll. Why: on mobile the soft keyboard settles AFTER the menu opens and
 *   scrolls the focused field into view — a one-shot measurement leaves the list
 *   floating at stale coordinates on the first open (PB-2909 reopen). Scroll is
 *   captured at the document level because the moving container is the modal body,
 *   not the window.
 * - Free space is measured against the VISIBLE viewport (visualViewport), not
 *   window.innerHeight. Why: with the keyboard up, innerHeight still spans the covered
 *   area on iOS, which kept dropping the list behind the keyboard. On desktop both are
 *   identical.
 * - Shrinks before it flips: the returned maxHeight caps the list to the space actually
 *   available below, and it only flips above when that space is unusable. Why: measuring
 *   against the visible viewport made almost every field flip once the keyboard opened,
 *   landing the list on top of the field being typed into. For a type-ahead that is worse
 *   than a short list — the decision on this ticket was that the user must be able to keep
 *   typing while suggestions show.
 * - The flip does NOT depend on the dropdown's measured height. Why: that dependency forced
 *   a two-frame open (below, then jump above), which is what read as "inconsistent".
 */
export function useAnchoredDropdownPosition({
  open,
  disabled,
  anchorRef,
  dropdownRef,
  matchAnchorWidth,
  preferBelow,
}: UseAnchoredDropdownPositionArgs): AnchoredDropdownStyle {
  const [style, setStyle] = useState<AnchoredDropdownStyle>({ left: 0, minWidth: 0 });

  useEffect(() => {
    if (!open || disabled || !anchorRef?.current) return;

    const widthFor = (rect: { width: number }) => ({
      minWidth: matchAnchorWidth ? rect.width : Math.max(rect.width, LIST_MIN_WIDTH_PX),
      width: matchAnchorWidth ? rect.width : undefined,
    });

    const position = () => {
      // Into LAYOUT-viewport coordinates, which is where `position: fixed` places the list and
      // where `visibleTop`/`visibleBottom` below are measured. A no-op everywhere but WebKit.
      const raw = anchorRef.current!.getBoundingClientRect();
      const offset = webkitVisualOffsets();
      const rect = {
        top: raw.top + offset.y,
        bottom: raw.bottom + offset.y,
        left: raw.left + offset.x,
        right: raw.right + offset.x,
        width: raw.width,
      };
      const size = widthFor(rect);
      const dropdownEl = dropdownRef.current;
      const dropdownHeight = dropdownEl?.offsetHeight ?? 0;
      const dropdownWidth = size.width ?? dropdownEl?.offsetWidth ?? size.minWidth;

      const vv = window.visualViewport;
      const visibleTop = vv?.offsetTop ?? 0;
      const visibleBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
      const spaceBelow = visibleBottom - rect.bottom - GAP;
      const spaceAbove = rect.top - visibleTop - GAP;

      const overflowsRight = rect.left + dropdownWidth > window.innerWidth;
      const horizontalPos = overflowsRight
        ? { right: window.innerWidth - rect.right, left: undefined }
        : { left: rect.left, right: undefined };

      // Type-aheads shrink to the space below rather than flip over the anchor — covering the
      // field being typed into is the one thing they must never do (PB-2909). Everything else
      // keeps the plain "flip when it does not fit" rule, which loses nothing for a Select and
      // gives it a taller list.
      const flipAbove = preferBelow
        ? spaceBelow < MIN_USABLE_BELOW_PX && spaceAbove > spaceBelow
        : dropdownHeight > spaceBelow && spaceAbove > spaceBelow;
      const maxHeight = preferBelow
        ? Math.max(MIN_HEIGHT_PX, Math.round(flipAbove ? spaceAbove : spaceBelow))
        : undefined;
      const next: AnchoredDropdownStyle = {
        ...(flipAbove
          ? { bottom: window.innerHeight - rect.top + GAP, top: undefined }
          : { top: rect.bottom + GAP, bottom: undefined }),
        ...horizontalPos,
        ...size,
        maxHeight,
      };
      // Bail out when nothing moved so scrolling inside the option list (which also
      // fires the captured scroll listener) doesn't re-render every frame.
      setStyle((prev) => (sameStyle(prev, next) ? prev : next));
    };

    // For preferBelow the first pass is already final — its flip decision does not depend on the
    // dropdown's measured height, so there is no "below, then jump above" frame. Other menus
    // still need the rAF, which is when offsetHeight becomes readable.
    position();
    let frame = requestAnimationFrame(position);

    const reposition = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(position);
    };
    document.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', reposition);
    vv?.addEventListener('scroll', reposition);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      vv?.removeEventListener('resize', reposition);
      vv?.removeEventListener('scroll', reposition);
    };
  }, [open, disabled, anchorRef, dropdownRef, matchAnchorWidth, preferBelow]);

  return style;
}
